import * as i0 from '@angular/core';
import { PLATFORM_ID, Injectable, Inject, Pipe, NgModule } from '@angular/core';
import * as i2$1 from '@angular/common';
import { isPlatformBrowser, formatCurrency, DOCUMENT, DatePipe } from '@angular/common';
import { Theme, PartnerDomains, ClientExposedUrl, SessionStorageKeys, LocalStorageKeys, ThemeQueryParamKey, FalcorRoutes, Features, UnpackMode, DAEnvironment, MFE_URLS } from '@vanguard/dap-common-models';
import { flattenDeep, get, forOwn, zip } from 'lodash-es';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone.js';
import utc from 'dayjs/plugin/utc.js';
import * as i2 from '@angular/common/http';
import * as i3 from '@angular/platform-browser';
import customParseFormat from 'dayjs/plugin/customParseFormat.js';
import isLeapYear from 'dayjs/plugin/isLeapYear.js';
import * as i1 from '@angular/router';
import { NavigationEnd } from '@angular/router';
import { from, map as map$1 } from 'rxjs';

class BrowserStorage {
    constructor(ssrService) {
        this.ssrService = ssrService;
    }
    getItem(key) {
        return this.runInBrowserOnly(() => {
            const value = this.getStorage().getItem(key) ?? null;
            if (value === null) {
                return null;
            }
            return JSON.parse(value);
        });
    }
    setItem(key, value) {
        this.runInBrowserOnly(() => {
            this.getStorage().setItem(key, JSON.stringify(value));
        });
    }
    removeItem(key) {
        this.runInBrowserOnly(() => {
            this.getStorage().removeItem(key);
        });
    }
    runInBrowserOnly(func) {
        return this.ssrService.isSSRrunning() ? undefined : func();
    }
}

class SSRService {
    constructor(platformId) {
        this.platformId = platformId;
    }
    skipSSRfor(func, ssrResult) {
        if (this.isSSRrunning()) {
            return ssrResult !== undefined ? ssrResult : SSRService.SSR_SKIPPED;
        }
        return func();
    }
    /**
     * @return true if function was called during SSR.
     */
    isSSRrunning() {
        return !isPlatformBrowser(this.platformId);
    }
}
SSRService.SSR_SKIPPED = Symbol('@ssrSkipped');
SSRService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SSRService, deps: [{ token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable });
SSRService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SSRService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SSRService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: Object, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }]; } });

class LocalStorageService extends BrowserStorage {
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(ssrService) {
        super(ssrService);
    }
    getStorage() {
        return localStorage;
    }
}
LocalStorageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: LocalStorageService, deps: [{ token: SSRService }], target: i0.ɵɵFactoryTarget.Injectable });
LocalStorageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: LocalStorageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: LocalStorageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: SSRService }]; } });

class SessionStorageService extends BrowserStorage {
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(ssrService) {
        super(ssrService);
    }
    getStorage() {
        return sessionStorage;
    }
    clearStorage() {
        this.runInBrowserOnly(() => this.getStorage().clear());
    }
}
SessionStorageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SessionStorageService, deps: [{ token: SSRService }], target: i0.ɵɵFactoryTarget.Injectable });
SessionStorageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SessionStorageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SessionStorageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: SSRService }]; } });

/* eslint-disable @typescript-eslint/no-explicit-any */
const isFunction = function (obj) {
    return typeof obj === 'function';
};
const isNumber = function (value) {
    return typeof value === 'number';
};
const isString = function (value) {
    return typeof value === 'string';
};
/**
 * Check if it is ES6 promise instance
 * @param val value to check
 */
const isPromise = function (val) {
    return val instanceof Promise;
};
function assertIsNotString(obj) {
    if (typeof obj === 'string') {
        throw new Error(`Passed value was a string, but we expected that it will not happen`);
    }
}
function assertIsString(obj) {
    if (typeof obj !== 'string') {
        throw new Error(`Passed value was not a string, but we expected that it will be a string`);
    }
}
function assertIsDefined(val) {
    if (val === undefined || val === null) {
        throw new Error(`Expected 'val' to be defined, but received ${String(val)}`);
    }
}
function isOneOfEnumKeys(enumList, val) {
    return String(val) in enumList;
}
function assertOneOfEnumKeys(enumList, val) {
    if (!isOneOfEnumKeys(enumList, val)) {
        throw new Error(`Passed ${String(val)} is not an enum value!`);
    }
}

/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Replaces first element which meets the condition. If condition
 * returns false for all elements in array then new element will be added to array
 * @param arr array copy
 * @param condition
 * @param addOrReplaceElement
 */
function addOrReplaceElementInArray(arr, condition, addOrReplaceElement) {
    const indexToReplace = arr.findIndex(condition);
    if (indexToReplace === -1) {
        return arr.concat([addOrReplaceElement]);
    }
    return copyArrayAndReplaceAtIndex(arr, indexToReplace, addOrReplaceElement);
}
async function map(array, mapFunction) {
    const arr = await array;
    return Promise.all(arr.map(mapFunction));
}
async function filter(array, predicate) {
    const arr = await array;
    const isKeepElements = await map(arr, predicate);
    return arr.filter((_, i) => isKeepElements[i]);
}
async function reduce(array, predicate, startParam) {
    const arr = await array;
    let res = startParam;
    for (const item of arr) {
        res = await predicate(res, item);
    }
    return res;
}
async function some(array, predicate) {
    const arr = await array;
    for (const item of arr) {
        if (await predicate(item)) {
            return true;
        }
    }
    return false;
}
async function forEach(array, onEach) {
    const arr = await array;
    for (const item of arr) {
        await onEach(item);
    }
}
/**
 *
 * @param array
 * @param delayInMs
 * @param onIteration
 * @param isImmediate
 * @returns {Promise<void>}
 */
async function asyncForEach(array = [], onIteration = noop, delayInMs = 0, isImmediate = false) {
    for (let i = 0; i < array.length; i++) {
        if (!delayInMs || (isImmediate && !i)) {
            await onIteration(array[i], i, array);
        }
        else {
            await delay(delayInMs).then(() => onIteration(array[i], i, array));
        }
    }
}
function copyArrayOmittingIndexes(arr, ...indexes) {
    return arr.filter((el, i) => !indexes.includes(i));
}
function copyArrayAndReplaceAtIndex(arr, index, replaceValue) {
    return arr.map((el, i) => (i === index ? replaceValue : el));
}
/**
 * @param len length of the new array
 * @param iterator optional function which will be called for each index to get initial element value
 */
function defineArray(len, iterator) {
    return Array.from(Array(len), (_, i) => iterator(i));
}
async function delay(millis = 0) {
    return new Promise(resolve => setTimeout(resolve, millis));
}
function equalIgnoreCase(val1, val2) {
    if (typeof val1 === 'string' && typeof val2 === 'string') {
        return val1.toLowerCase() === val2.toLowerCase();
    }
    return false;
}
/**
 * It adds delimiter to the first level of depth of arrays. And flattens it.
 * It is the same as regular string join but for array of arrays.
 *
 * For example with input: [[1, [2]], [3], [4]] result will be: [1, 2, delimiter, 3, delimiter, 4]
 *
 * @param {Array<Array>} arrays array of arrays
 * @param delimiter element to insert between arrays
 * @returns {Array} flattened array with added delimiter elements.
 */
function flattenJoinArrays(arrays, delimiter) {
    return flattenDeep(arrays.map((arrEl, i) => {
        const isLast = i === arrays.length - 1;
        return isLast ? arrEl : [arrEl, delimiter];
    }));
}
const getByPath = get;
const isNil = (val) => val === null || val === undefined;
/**
 * Waits for all promises on object properties of depth 1
 * @param obj
 */
const loadAsyncObjectProps = async function (obj = {}) {
    const promises = [];
    const keys = [];
    forOwn(obj, function (value, key) {
        if (isPromise(value)) {
            promises.push(value);
            keys.push(key);
        }
    });
    const resultPairs = zip(keys, await Promise.all(promises));
    return { ...obj, ...Object.fromEntries(resultPairs) };
};
const noop = () => { };
/**
 * Returns function which returns inverted result of original function
 * @param func function to wrap
 */
const not = (func) => (...args) => !func(...args);
const notNil = function (value) {
    return value !== null && value !== undefined;
};
/**
 * @param func funtion to run
 * @param condition condition to check
 * @param args
 */
const runIf = function (condition, func) {
    return function (...args) {
        const isMustToRun = isFunction(condition) ? condition() : condition;
        return isMustToRun ? func(...args) : noop();
    };
};

function empty() {
    return {};
}
/**
 * workaround to get keys from Enums as string instead of union of enum strings
 */
function getEnumValues(enumClass) {
    return Object.values(enumClass);
}

const jsVariablePlaceholderRegex = /\${[^{]+}/g;
/**
 * interpolate('hello ${name}', {name: 'John'})
 * will return: "hello John"
 *
 * @param string
 * @param namedVariables
 * @returns {any}
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const interpolate = function (string, namedVariables) {
    return string.replace(jsVariablePlaceholderRegex, match => {
        const namedVariable = namedVariables[match.slice(2, -1).trim()];
        return namedVariable === undefined ? match : namedVariable;
    });
};

class DateHelper {
    static testDateString(dateString) {
        if (!dateString) {
            return false;
        }
        try {
            const dayJsDateString = dayjs(dateString, DateHelper.dateFormatDayjs).format(DateHelper.dateFormatDayjs);
            return dayJsDateString === dateString;
        }
        catch (e) {
            return false;
        }
    }
    /**
     * Checks if there is at least one invalid date string in an array and returns false
     */
    static testDateStringArray(dateStringArray) {
        return dateStringArray.every(DateHelper.testDateString);
    }
    static getCurrentDateEST() {
        dayjs.extend(utc);
        dayjs.extend(timezone);
        const serverDate = dayjs().tz(this.serverTimezone).toDate();
        return DateHelper.getYMDDate(serverDate);
    }
    static getFormatDate(date, format) {
        return date === null ? null : dayjs(date).format(format);
    }
    static getYMDDate(date) {
        return date === null ? null : dayjs(date).format('YYYY-MM-DD');
    }
    static getMDYDate(date) {
        return date === null ? null : dayjs(date).format('MM/DD/YY');
    }
    static getYMDate(date) {
        return date === null ? null : dayjs(date).format('YYYY-MM');
    }
    static getMDDate(date) {
        return date === null ? null : dayjs(date).format('MM-DD');
    }
    static addMonthsToDateString(dateString, monthNumber) {
        const result = this.testDateString(dateString) ? dayjs(dateString).add(monthNumber, 'M').toDate() : null;
        return result;
    }
    static addDaystoDateString(dateString, daysToAdd) {
        const result = this.testDateString(dateString) ? dayjs(dateString).add(daysToAdd, 'd').toDate() : null;
        return result;
    }
    static addYearsToDateString(dateString, yearNumber) {
        const result = this.addMonthsToDateString(dateString, yearNumber * 12);
        return result;
    }
    static getDateFromDateString(dateString) {
        const result = this.testDateString(dateString) ? dayjs(dateString).toDate() : null;
        return result;
    }
    /**
     * Compares 2 date strings as if they were in the same year
     * @param dateString1 'yyy1-m1-d1'
     * @param dateString2 'yyy2-m2-d2'
     *
     * @returns 'm1-d1'.localeCompare('m2-d2') (-1, 0, 1) or
     *          null if any of the date strings does not pass dayjs() validation
     */
    static compareDateStringsNoYear(dateString1, dateString2) {
        if (!this.testDateStringArray([dateString1, dateString2])) {
            return null;
        }
        return DateHelper.compareDateStrings(dateString1.slice(5), dateString2.slice(5), true);
    }
    /**
     * Compares two full date strings.
     * We need skipValidation in order to reuse the code for the upper function
     */
    static compareDateStrings(dateString1, dateString2, skipStringsValidation = false) {
        if (!skipStringsValidation && !this.testDateStringArray([dateString1, dateString2])) {
            return null;
        }
        return dateString1.localeCompare(dateString2);
    }
    static getCurrentDate() {
        return new Date();
    }
    /**
     * Returns the current year as four digit number
     */
    getCurrentYear() {
        return this.getCurrentDate().getFullYear();
    }
    /**
     * Returns current date in format 'YYYY-MM-DD'
     */
    getFullDateString() {
        const date = this.getCurrentDate();
        const year = date.getFullYear().toString();
        const month = this.normalize((date.getMonth() + 1).toString());
        const day = this.normalize(date.getDate().toString());
        return `${year}-${month}-${day}`;
    }
    /**
     * Returns timestamp string in ISO format, f.e. '2020-02-18T21:56:16.907'
     */
    getTestTimestampString(numberDaysInThePast = 0) {
        const date = this.getCurrentDate();
        date.setDate(date.getDate() - numberDaysInThePast);
        return date.toISOString().slice(0, -1);
    }
    getUTCDateStringNoMilliseconds(date = this.getCurrentDate()) {
        const year = date.getUTCFullYear().toString();
        const month = this.normalize((date.getUTCMonth() + 1).toString());
        const day = this.normalize(date.getUTCDate().toString());
        const hours = this.normalize(date.getUTCHours().toString());
        const minutes = this.normalize(date.getUTCMinutes().toString());
        const seconds = this.normalize(date.getUTCSeconds().toString());
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
    }
    getDateMinsInFuture(diff) {
        return new Date(this.getCurrentDate().getTime() + diff * 60000);
    }
    getCurrentDate() {
        return DateHelper.getCurrentDate();
    }
    normalize(item) {
        return item.length === 1 ? `0${item}` : item;
    }
}
// Date format for application YYYY-MM-DD (Dayjs YYYY-MM-DD)
DateHelper.dateFormatDayjs = 'YYYY-MM-DD';
DateHelper.serverTimezone = 'America/New_York';

class AmountHelper {
    /**
     * Returns a string with the name of the icon depending on the goal type
     * @param amount Number
     * @param showCurrency Boolean. Could return amount with or without $ in the start
     * @param showAsNumber Boolean. Could return rounded amount as full number without K/M suffix
     *
     * @returns string
     */
    static round(amount, { showCurrency = true, showAsNumber = false } = {}) {
        const absY = Math.abs(amount);
        let result;
        if (absY < 1000) {
            result = `${amount}`;
        }
        else if (absY < 999950) {
            result = showAsNumber ? `${this.divide(amount, 1000) * 1000}` : `${this.divide(amount, 1000)}K`;
        }
        else {
            result = showAsNumber ? `${this.divide(amount, 1000000) * 1000000}` : `${this.divide(amount, 1000000)}M`;
        }
        return showCurrency ? `$${result}` : result;
    }
    /**
     * Returns the amount with decimal places after the dot, if it is not zero
     *
     * @returns string
     */
    static divide(amount, divider, decimalPlaces = 1) {
        const decimalDigitPlaceholder = Math.pow(10, decimalPlaces);
        return Math.round((amount * decimalDigitPlaceholder) / divider) / decimalDigitPlaceholder;
    }
    /**
     * Returns amount with truncated decimal part
     * @param amount Number
     *
     * @returns string
     */
    static trunc(amount) {
        return formatCurrency(Math.trunc(amount), 'en-US', '$', 'USD', '1.0-0');
    }
}

class StringHelper {
    /**
     * Determines if the value is valid for JSON parsing
     *
     * @param value - any string
     */
    static isJsonString(value) {
        try {
            JSON.parse(value);
        }
        catch (e) {
            return false;
        }
        return true;
    }
}

/**
 * workaround to get reverse mapping for string-based enums
 */
const reverseMapStringEnum = (e) => {
    const reversedEnum = {};
    Object.keys(e).forEach(k => (reversedEnum[e[k]] = k));
    return Object.assign(reversedEnum, e);
};
const ReverseMappedTheme = reverseMapStringEnum(Theme);
const ReverseMappedPartnerDomains = reverseMapStringEnum(PartnerDomains);
const identifyTheme = (url) => {
    let hostname;
    try {
        hostname = new URL(url).hostname;
    }
    catch (e) {
        hostname = '';
    }
    const partnerDomain = Object.values(PartnerDomains).find((partner) => hostname.includes(partner));
    if (partnerDomain) {
        const output = ReverseMappedTheme[ReverseMappedPartnerDomains[partnerDomain]];
        return output;
    }
    return undefined;
};

class tagReplacer {
    constructor(tagIdentifier) {
        this.replaceTag = (document, url) => {
            let documentCopy = document;
            const selectedTheme = identifyTheme(url);
            if (selectedTheme) {
                const tagReplacement = this.buildReplacementTag(selectedTheme);
                documentCopy = documentCopy.replace(this.tagIdentifier, tagReplacement);
            }
            return documentCopy;
        };
        this.tagIdentifier = tagIdentifier;
    }
}

class themeTagReplacer extends tagReplacer {
    constructor() {
        super(...arguments);
        this.buildReplacementTag = (theme) => {
            return `<meta name="theme" content="${theme}"/>`;
        };
    }
}
const replaceThemeMetaTag = (document, url = '') => {
    return new themeTagReplacer(/<meta\s+name="theme"\s+content="default"\s*\/?>/).replaceTag(document, url);
};

const tokens = {
    apollo: {
        brandName: 'INVEST',
        brandNameNbsp: 'INVEST',
        brandNameFull: 'INVEST',
        brandNameFullNbsp: 'INVEST'
    },
    default: {
        brandName: 'Digital Advisor',
        brandNameNbsp: 'Digital&nbsp;Advisor',
        brandNameFull: 'Vanguard Digital Advisor',
        brandNameFullNbsp: 'Vanguard Digital&nbsp;Advisor'
    },
    personalAdvisor: {
        brandName: 'Personal Advisor',
        brandNameNbsp: 'Personal&nbsp;Advisor',
        brandNameFull: 'Vanguard Personal Advisor',
        brandNameFullNbsp: 'Vanguard Personal&nbsp;Advisor'
    }
};
const recursiveReplace = (content, tokens) => {
    if (content) {
        Object.keys(content).forEach((key) => {
            if (isString(content[key])) {
                content[key] = interpolate(content[key], tokens);
            }
            else if (typeof content === 'object') {
                recursiveReplace(content[key], tokens);
            }
        });
    }
    return content;
};
/**
 * Replace brand name tokens regarding the selected theme
 * @param content the content json
 * @param theme selected theme
 */
const replaceBrandNameTokens = (content, theme) => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const selectedTokens = tokens[theme] ?? tokens.default;
    return recursiveReplace(content, selectedTokens);
};

class titleTagReplacer extends tagReplacer {
    constructor() {
        super(...arguments);
        this.buildReplacementTag = (theme) => {
            return `<title tabindex="-1">${tokens[theme].brandNameFull}</title>`;
        };
    }
}
const replaceTitleTag = (document, url = '') => {
    return new titleTagReplacer(/<title.*<\/title>/).replaceTag(document, url);
};

class PercentageCalculator {
    /**
     * Returns an Array of OutputValueWithPercentage, where the object contains both the input amount and the associated
     * percentage amount. The input values will be rounded to the given precision.
     * @param inputValueWithId InputValueWithId[]
     * @param precision Number. Default value = 0
     *
     * @returns OutputValueWithPercentage[]
     */
    static calculatePercentage(inputValueWithId, precision = 0) {
        const length = inputValueWithId.length;
        if (length === 0) {
            return [];
        }
        const outPutValueWithPercentages = inputValueWithId.map(account => {
            return { ...account, percentage: 0 };
        });
        let total = 0;
        for (let i = length - 1; i >= 0; i--) {
            total += inputValueWithId[i].num;
        }
        const powPrecision = Math.pow(10, precision);
        const pow100 = 100 * powPrecision;
        let check100 = 0;
        for (let i = length - 1; i >= 0; i--) {
            inputValueWithId[i].num = (100 * inputValueWithId[i].num) / total;
            check100 += outPutValueWithPercentages[i].percentage = Math.round(inputValueWithId[i].num * powPrecision);
        }
        if (check100 !== pow100) {
            this.compensateDifferenceFrom100Percent(check100, precision, inputValueWithId, outPutValueWithPercentages);
        }
        if (powPrecision > 1) {
            for (let i = 0; i < length; i++) {
                outPutValueWithPercentages[i].percentage = outPutValueWithPercentages[i].percentage / powPrecision;
            }
        }
        return outPutValueWithPercentages;
    }
    static compensateDifferenceFrom100Percent(differenceFrom100Percent, precision, inputPercents, output) {
        const powPrecision = Math.pow(10, precision);
        const powerTo100 = 100 * powPrecision;
        const length = inputPercents.length;
        const totalDiff = differenceFrom100Percent - powerTo100;
        const roundGrain = 1;
        let grainCount = Math.abs(totalDiff);
        const diffs = new Array(length);
        for (let i = 0; i < length; i++) {
            diffs[i] = Math.abs(output[i].percentage - inputPercents[i].num * powPrecision);
        }
        while (grainCount > 0) {
            let idx = 0;
            let maxDiff = diffs[0];
            for (let i = 1; i < length; i++) {
                if (maxDiff < diffs[i]) {
                    idx = i;
                    maxDiff = diffs[i];
                }
            }
            output[idx].percentage =
                differenceFrom100Percent > powerTo100 ? output[idx].percentage - roundGrain : output[idx].percentage + roundGrain;
            diffs[idx] -= roundGrain;
            grainCount--;
        }
        return output;
    }
}

class WebComponentLoadDetailsHelper {
    static getWebComponentFailedLoadDetails(clientExposedUrl) {
        return WebComponentLoadDetailsHelper.webComponentTraceDetails.has(clientExposedUrl)
            ? WebComponentLoadDetailsHelper.webComponentTraceDetails.get(clientExposedUrl)
            : WebComponentLoadDetailsHelper.defaultFailedWebComponentDetails;
    }
}
WebComponentLoadDetailsHelper.defaultFailedWebComponentDetails = { failedApp: 'not defined', team: 'not defined' };
WebComponentLoadDetailsHelper.webComponentTraceDetails = new Map([
    [ClientExposedUrl.WEBCOMPONENT_CASHFLOW, { failedApp: 'FL0', team: 'Falkor' }],
    [ClientExposedUrl.WEBCOMPONENT_CUSTOM_GOAL, { failedApp: 'CU5', team: 'Falkor' }],
    [ClientExposedUrl.WEBCOMPONENT_PERSONALIZED_PORTFOLIO, { failedApp: 'KYE', team: '4D' }],
    [ClientExposedUrl.WEBCOMPONENT_EMERGENCY_SAVINGS, { failedApp: 'ES7', team: '4D' }],
    [ClientExposedUrl.WEBCOMPONENT_PORTFOLIO_CUSTOMIZATION, { failedApp: 'YOU', team: 'NEBULA' }],
    [ClientExposedUrl.WEBCOMPONENT_DAP_APPOINTMENT_SCHEDULER, { failedApp: 'QKU', team: 'PEGASUS' }],
    [ClientExposedUrl.WEBCOMPONENT_FIRST_MONTH_EXPERIENCE, { failedApp: 'FIF', team: 'Quasar' }],
    [ClientExposedUrl.WEBCOMPONENT_TAX_LOSS_OPTION, { failedApp: 'MPZ', team: 'Quasar' }],
    [ClientExposedUrl.WEBCOMPONENT_ACTIVITY_LOG, { failedApp: 'JFD', team: 'Quasar' }],
    [ClientExposedUrl.WEBCOMPONENT_DAP_APPOINTMENT_SCHEDULER, { failedApp: 'QKU', team: 'PEGASUS' }],
    [ClientExposedUrl.WEBCOMPONENT_ACCOUNTS_AND_HOLDINGS, { failedApp: 'GQD', team: 'Deadliest Cash' }],
    [ClientExposedUrl.WEBCOMPONENT_PERFORMANCE, { failedApp: 'UHV', team: 'NEBULA' }],
    [ClientExposedUrl.WEBCOMPONENT_RISKFLOW, { failedApp: 'RFA', team: 'Mantis' }],
    [ClientExposedUrl.WEBCOMPONENT_FAQ, { failedApp: 'FQW', team: 'ACME' }],
    [ClientExposedUrl.WEBCOMPONENT_SOCIAL_SECURITY_EXPERIENCE, { failedApp: 'HUU', team: 'Awesome Force' }],
    [ClientExposedUrl.WEBCOMPONENT_REQUIRED_MINIMUM_DISTRIBUTION, { failedApp: 'RQD', team: 'Awesome Force' }],
    [ClientExposedUrl.WEBCOMPONENT_TRANSACTION_MANAGE_TRANSFERS, { failedApp: 'TMT', team: 'Deadliest Cash' }],
    [ClientExposedUrl.WEBCOMPONENT_ACTIVE_IN_ADVICE, { failedApp: 'AA5', team: 'Deadliest Cash' }],
    [ClientExposedUrl.WEBCOMPONENT_CONFIRMATION_PAGE, { failedApp: 'CP8', team: 'Falkor' }],
    [ClientExposedUrl.WEBCOMPONENT_DATA_AGGREGATION, { failedApp: 'DYJ', team: 'ACME' }],
    [ClientExposedUrl.WEBCOMPONENT_MONEY_TO_WORK, { failedApp: 'MTW', team: 'Sundaes' }],
    [ClientExposedUrl.WEBCOMPONENT_FINANCIAL_GOAL_TRADEOFFS, { failedApp: 'MTQ', team: 'Star-Lord' }],
    [ClientExposedUrl.WEBCOMPONENT_OFFER_UPGRADE_DOWNGRADE, { failedApp: 'OUD', team: 'Benatar' }],
    [ClientExposedUrl.WEBCOMPONENT_PORTFOLIO_BUILDER, { failedApp: 'PBJ', team: 'Sundaes' }],
    [ClientExposedUrl.WEBCOMPONENT_VMAP_POST_CONVERSION, { failedApp: 'PC7', team: 'Gravity' }],
    [ClientExposedUrl.WEBCOMPONENT_FINANCIAL_PROFILE, { failedApp: 'QYJ', team: 'ACME' }],
    [ClientExposedUrl.WEBCOMPONENT_ACCOUNTS_AND_CONTRIBUTIONS, { failedApp: 'YAC', team: 'ACME' }],
    [ClientExposedUrl.WEBCOMPONENT_MY_PLAN_PAGE, { failedApp: 'ZS0', team: 'Star-Lord' }],
    [ClientExposedUrl.WEBCOMPONENT_TOOLS_RESOURCES, { failedApp: 'TMC', team: 'Kratos' }],
    [ClientExposedUrl.WEBCOMPONENT_HEALTH_CARE_COST_ESTIMATOR, { failedApp: 'JYU', team: 'Kratos' }],
    [ClientExposedUrl.WEBCOMPONENT_MEDICARE_MATCH, { failedApp: 'LL4', team: 'Kratos' }],
    [ClientExposedUrl.WEBCOMPONENT_EXTRAORDINARY_EXPENSES, { failedApp: 'EIE', team: 'Kratos' }],
    [ClientExposedUrl.WEBCOMPONENT_ADVICE_FUTURE_TRANSACTION, { failedApp: 'MAG', team: 'Deadliest Cash' }]
]);

/* eslint-disable @typescript-eslint/no-explicit-any */
class BrowserStorageUtilsService {
    constructor(ssrService) {
        this.runInBrowserOnly = runIf.bind(this, () => !this.ssrService.isSSRrunning());
        this.setItemToStorage = this.runInBrowserOnly((key, value) => {
            this.assertValidKey(key);
            if (isOneOfEnumKeys(SessionStorageKeys, key)) {
                return sessionStorage.setItem(String(key), JSON.stringify(value));
            }
            return localStorage.setItem(String(key), JSON.stringify(value));
        });
        this.clearSessionStorage = this.runInBrowserOnly(() => {
            for (const key in SessionStorageKeys) {
                if (Object.prototype.hasOwnProperty.call(SessionStorageKeys, key)) {
                    sessionStorage.removeItem(key);
                }
            }
        });
        this.clearStorageKey = this.runInBrowserOnly((key) => {
            sessionStorage.removeItem(key);
        });
        this.clearLocalStorageKey = this.runInBrowserOnly((key) => {
            localStorage.removeItem(key);
        });
        this.getItemFromStorage = this.runInBrowserOnly((key) => {
            this.assertValidKey(key);
            if (isOneOfEnumKeys(SessionStorageKeys, key)) {
                return this.get(sessionStorage, key);
            }
            return this.get(localStorage, key);
        });
        // sets cookie
        // if expirationDays is omitted, the cookie will have session expiration length
        this.setCookie = this.runInBrowserOnly((name, value, expirationDays = null) => {
            let expires = '';
            if (expirationDays) {
                const d = new Date();
                d.setTime(d.getTime() + expirationDays * 24 * 60 * 60 * 1000);
                expires = `expires=${d.toUTCString()};`;
            }
            document.cookie = `${name}=${value};${expires}path=/`;
        });
        // get relative URL starting with forward slash
        // preserves query params
        this.getRelativeUrl = this.runInBrowserOnly(() => window.location.href.replace(/^(?:\/\/|[^/]+)*/, ''));
        this.ssrService = ssrService;
    }
    get(storage, key) {
        // @ts-ignore
        return JSON.parse(storage.getItem(String(key)));
    }
    assertValidKey(key) {
        if (isOneOfEnumKeys(SessionStorageKeys, key) && isOneOfEnumKeys(LocalStorageKeys, key)) {
            throw new Error('Ambiguous key');
        }
        if (!isOneOfEnumKeys(SessionStorageKeys, key) && !isOneOfEnumKeys(LocalStorageKeys, key)) {
            throw new Error("Can't store undeclared key");
        }
    }
}
BrowserStorageUtilsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: BrowserStorageUtilsService, deps: [{ token: SSRService }], target: i0.ɵɵFactoryTarget.Injectable });
BrowserStorageUtilsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: BrowserStorageUtilsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: BrowserStorageUtilsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: SSRService }]; } });

/* eslint-disable @typescript-eslint/no-unnecessary-condition */
class BrowserUtilsService {
    constructor(ssrService) {
        this.ssrService = ssrService;
    }
    isMozilla() {
        return (this.runInBrowserOnly(() => {
            return navigator.userAgent.toLowerCase().includes('firefox');
        }) ?? false);
    }
    listenWindowEvent(event, func) {
        return this.runInBrowserOnly(() => {
            window.addEventListener(event, func);
            return window.removeEventListener.bind(window, event, func);
        });
    }
    setCookie(name, value) {
        return this.runInBrowserOnly(() => {
            if (document) {
                document.cookie = `${name}=${value}; path=/; domain=.vanguard.com;`;
            }
            else {
                throw new Error('document not found');
            }
        });
    }
    getCookie(name) {
        return this.runInBrowserOnly(() => {
            const cookie = document?.cookie || '';
            const nameWithEqualSign = `${name}=`;
            const decodedCookie = decodeURIComponent(cookie);
            const indexOfCookieData = decodedCookie.indexOf(nameWithEqualSign);
            if (indexOfCookieData === -1) {
                return undefined;
            }
            const valueStartIndex = indexOfCookieData + nameWithEqualSign.length;
            const valueEndIndex = decodedCookie.indexOf(';', indexOfCookieData);
            return decodedCookie.substring(valueStartIndex, valueEndIndex === -1 ? decodedCookie.length : valueEndIndex);
        });
    }
    deleteCookie(name) {
        return this.runInBrowserOnly(() => {
            if (document) {
                document.cookie = `${name}=; path=/; domain=.vanguard.com; expires = Thu, 01 Jan 1970 00:00:00 GMT`;
            }
            else {
                throw new Error('document not found');
            }
        });
    }
    getMetaTagValue(name) {
        return this.runInBrowserOnly(() => {
            if (document) {
                return document.getElementsByTagName('meta').namedItem(name)?.content;
            }
            return 'prod';
        });
    }
    isProd() {
        const env = this.runInBrowserOnly(() => this.getMetaTagValue('env')?.trim());
        const isNotProdEnv = equalIgnoreCase(env, 'dev') || equalIgnoreCase(env, 'sat');
        return !isNotProdEnv;
    }
    isInternal() {
        const env = this.runInBrowserOnly(() => this.getMetaTagValue('domainType')?.trim());
        return equalIgnoreCase(env, 'internal');
    }
    runInBrowserOnly(func) {
        return this.ssrService.isSSRrunning() ? undefined : func();
    }
}
BrowserUtilsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: BrowserUtilsService, deps: [{ token: SSRService }], target: i0.ɵɵFactoryTarget.Injectable });
BrowserUtilsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: BrowserUtilsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: BrowserUtilsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: SSRService }]; } });

/* eslint-disable @typescript-eslint/no-explicit-any */
class FalcorWrapperService {
    init(falcorService) {
        this.falcorService = falcorService;
    }
    async get(pathTemplate) {
        return this.getFalcorService().get(pathTemplate);
    }
    // eslint-disable-next-line @typescript-eslint/require-await
    async call(opts, param2, param3) {
        return this.getFalcorService().call(opts, param2, param3);
    }
    invalidatePathCache(pathSets, ...args) {
        this.getFalcorService().invalidatePathCache(pathSets, ...args);
    }
    getFalcorService() {
        if (!this.falcorService) {
            throw new Error('Digital Advisor Common Library: Falcor Service is not initialized. Call init() first.');
        }
        return this.falcorService;
    }
}
FalcorWrapperService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: FalcorWrapperService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
FalcorWrapperService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: FalcorWrapperService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: FalcorWrapperService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

/** @dynamic */
class ThemeService {
    constructor(document, ssrService, browserUtils, meta) {
        this.document = document;
        this.ssrService = ssrService;
        this.browserUtils = browserUtils;
        this.meta = meta;
    }
    /**
     *getSelectedTheme - to return the theme value only
     * from meta tag first
     * can be overwritten by Theme From Query param
     */
    getSelectedTheme() {
        let selectedTheme;
        // TODO: Temporary solution until a better fix is found for the long term. (Tech debt: TBA-820)
        try {
            selectedTheme = this.getThemeFromMetaTag();
        }
        catch {
            selectedTheme = Theme.DEFAULT;
        }
        const themeFromQuery = this.getThemeFromUrl(ThemeQueryParamKey.THEME_ALL);
        if (!this.browserUtils.isProd() && themeFromQuery) {
            selectedTheme = themeFromQuery;
        }
        return selectedTheme;
    }
    /**
     *initTheme - to set correct styles/class-names only
     */
    initTheme() {
        if (!this.ssrService.isSSRrunning()) {
            const selectedTheme = this.getSelectedTheme();
            const themeStylesFromQuery = this.getThemeFromUrl(ThemeQueryParamKey.STYLES);
            if (!this.browserUtils.isProd() && themeStylesFromQuery) {
                this.setClassForTheme(themeStylesFromQuery);
            }
            this.setClassForTheme(selectedTheme);
        }
    }
    setClassForTheme(theme) {
        if (theme !== Theme.DEFAULT) {
            this.addClassToHtmlTag(theme);
        }
    }
    getThemeFromMetaTag() {
        const metaElement = this.meta.getTag('name=theme');
        if (metaElement && metaElement.content) {
            const foundTheme = getEnumValues(Theme).find(theme => theme === metaElement.content);
            return foundTheme;
        }
        throw new Error('Theme meta tag not found or has no value');
    }
    /**
     *getThemeFromUrl - returns theme value from query parameter
     * only if query parameter key is matching the passed themeQueryToCheck
     * and parameter value is matching Theme enum
     * otherwise - undefined
     */
    getThemeFromUrl(themeQueryToCheck) {
        const url = new URL(this.document.location.href);
        const themeSearchParam = url.searchParams.get(themeQueryToCheck);
        const isValidTheme = getEnumValues(Theme).includes(themeSearchParam);
        return isValidTheme ? themeSearchParam : undefined;
    }
    addClassToHtmlTag(className) {
        this.document.documentElement.classList.add(className);
    }
}
ThemeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ThemeService, deps: [{ token: DOCUMENT }, { token: SSRService }, { token: BrowserUtilsService }, { token: i3.Meta }], target: i0.ɵɵFactoryTarget.Injectable });
ThemeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ThemeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ThemeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: SSRService }, { type: BrowserUtilsService }, { type: i3.Meta }]; } });

const MF_CONTENT_PATH = '/content';
class ContentService {
    constructor(falcor, http, themeService) {
        this.falcor = falcor;
        this.http = http;
        this.themeService = themeService;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.contentPromiseCache = {};
        this.presetSourceUrl = false;
        this.presetTheme = Theme.DEFAULT;
    }
    /**
     * Method that sets URL(to request content from) and theme.
     * Allows to call getContent without any parameters.
     * @param {string} sourceUrl - Url to get the content from.
     * @returns {void} - does not return anything.
     */
    init(sourceUrl) {
        this.presetSourceUrl = sourceUrl;
        this.presetTheme = this.themeService.getThemeFromMetaTag();
    }
    /**
     * Method that returns content.
     * If you called init, then you can call this without parameters.
     * @param {string} flow - Name of content flow(file) you want to get.
     * @param {string} theme - Content of what Theme you want to get. Theme enum in common package.
     * @param {string} sourceUrl - If passed, tries to get the content from specified url.
     * @param {boolean} withCredentials - Whether the HTTP GET request should be sent with credentials.
     * @returns {Promise<T>} - returns promise
     */
    // eslint-disable-next-line max-lines-per-function
    async getContent(flow = 'content', theme = this.presetTheme, sourceUrl, withCredentials = true) {
        if (!isNil(this.contentPromiseCache[flow])) {
            return this.contentPromiseCache[flow];
        }
        const appUrl = isNil(sourceUrl) && this.presetSourceUrl ? this.presetSourceUrl : sourceUrl;
        try {
            if (appUrl) {
                let themeFolderName = '';
                if (theme !== Theme.DEFAULT) {
                    themeFolderName = `/${theme}`;
                }
                const url = `${appUrl}${MF_CONTENT_PATH}${themeFolderName}/${flow}.json`;
                const contentPromise = this.http.get(url, { withCredentials }).toPromise();
                this.contentPromiseCache[flow] = contentPromise.then(content => replaceBrandNameTokens(content, theme));
                return this.contentPromiseCache[flow];
            }
            const content = await this.falcor.get({
                path: FalcorRoutes.CONTENT,
                pathArgs: [flow, theme]
            });
            return content;
        }
        catch (error) {
            throw new Error(`Digital Advisor Common Library: Can not retrieve data from Content Service. ${error}`);
        }
    }
}
ContentService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ContentService, deps: [{ token: FalcorWrapperService }, { token: i2.HttpClient }, { token: ThemeService }], target: i0.ɵɵFactoryTarget.Injectable });
ContentService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ContentService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ContentService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: FalcorWrapperService }, { type: i2.HttpClient }, { type: ThemeService }]; } });

/**
 * Date Helper Service
 * Helps with calculating dates in different situations
 *
 * @module RetirementModule
 * @class DateHelperService
 * @author Nikolay_Aleksiev@vanguard.com
 */
class DateHelperService {
    constructor() {
        this.getCurrentDayjs = dayjs;
        this.dateHelper = new DateHelper();
        /**
         * Retrieves the year from date string and returns the year as four digit number
         *
         * @param dateString string containing recognizable date usually 'YYYY-MM-DD'
         */
        this.getYearValueFromDateString = (dateString = '') => parseInt(dateString.substring(0, 4), 10);
        /**
         * Calculates the corresponding date in the current year for a specific date
         *
         * @param dateString string containing recognizable date usually 'YYYY-MM-DD'
         */
        this.getDateForCurrentYear = (dateString = '') => dateString ? `${this.getCurrentYear()}${this.getLeapSafeMonthAndDay(dateString)}` : null;
        /**
         * Returns the current year as four digit number
         */
        this.getCurrentYear = () => this.dateHelper.getCurrentYear();
        /**
         * Returns the current date as a new Date instance
         */
        this.getCurrentDate = () => this.dateHelper.getCurrentDate();
        /**
         * Returns current date in format 'YYYY-MM-DD'
         */
        this.getFullDateString = () => this.dateHelper.getFullDateString();
        dayjs.extend(customParseFormat);
        dayjs.extend(isLeapYear);
    }
    /**
     * Returns the middle of the year date in format 'YYYY-MM-DD'
     *
     * @param year four digit year either in string or number format
     */
    getMiddleDateOfYear(year) {
        return `${this.getYearToUse(year)}-07-02`;
    }
    /**
     * Returns the end of the year date in format 'YYYY-MM-DD'
     *
     * @param year four digit year either in string or number format
     */
    getEndDateOfYear(year) {
        return `${this.getYearToUse(year)}-12-31`;
    }
    /**
     * Returns date in string format 'YYYY-MM-DD'
     *
     * @param year four digit number representing year
     */
    getStartDateOfYear(year) {
        return `${this.getYearToUse(year)}-01-01`;
    }
    /**
     * Adds the passed years number to the passed date string
     * returns the calculated date as string in format 'YYYY-MM-DD'
     *
     * If the calculated year is in not a leap year, return the leap
     * safe date.
     *
     * Ex.) addYearsToDateString('2016-02-29', 4); would return
     *  '2020-02-29' and addYearsToDateString('2016-02-29', 5); would
     *  return '2021-02-28'.
     *
     * @param dateString date in string format 'YYYY-MM-DD'
     * @param years number of years to be added to the passed date string
     */
    addYearsToDateString(dateString, years) {
        if (!dateString) {
            return null;
        }
        const resultYearString = (this.getYearValueFromDateString(dateString) + years).toString();
        const monthAndDayString = this.getMonthAndDay(dateString);
        return (resultYearString +
            (dayjs(`${resultYearString}-01-01`, 'YYYY-MM-DD').isLeapYear() ? monthAndDayString : this.getLeapSafeMonthAndDay(monthAndDayString)));
    }
    /**
     * Adds the passed years and months number to the passed date string
     * returns the calculated date as string in format 'YYYY-MM-DD'
     *
     * @param dateString date in string format 'YYYY-MM-DD'
     * @param years number of years to be added to the passed date string
     * @param months number of months to be added to the passed date string
     */
    addYearsAndMonthsToDateString(dateString, years, months) {
        const validMonths = isNil(months) ? 0 : months;
        const newDate = dayjs(dateString, 'YYYY-MM-DD').add(years, 'year').add(validMonths, 'month');
        return newDate.format('YYYY-MM-DD');
    }
    /**
     * Formats any recognizable date string into new format sent as argument
     *
     * @param inputDate the date to be formatted in string recognizable format
     * @param format the format to be applied to the date before returned
     */
    formatDateString(inputDate, format) {
        const datePipe = new DatePipe('en-US');
        return datePipe.transform(inputDate, format);
    }
    /**
     * Formats any recognizable date string into the compatible format for Us location
     *
     * @param inputDate the date to be formatted in  compatible format
     */
    fullUsDate(inputDate) {
        let formattedDate = inputDate;
        if (!isNil(inputDate)) {
            formattedDate = `${this.formatDateString(inputDate, 'MMMM d, y')} GMT-5`;
        }
        return formattedDate;
    }
    /**
     * Calculates and returns the exact number of full years passed
     * between date in the past and present
     * Now another date can be passed as second parameter
     * Should be later than the first date to get positive number
     *
     * @param dateStr Date in the past in string format 'YYYY-MM-DD'
     * @param date Date later than the first one
     *
     * @returns {number} - years passed
     */
    calculateYearsPassedSinceDate(dateStr, date) {
        const today = date !== null && date !== undefined ? new Date(this.fullUsDate(date)) : this.dateHelper.getCurrentDate();
        const pastDate = new Date(this.fullUsDate(dateStr));
        const month = today.getMonth() - pastDate.getMonth();
        let years = today.getFullYear() - pastDate.getFullYear();
        if (month < 0 || (month === 0 && today.getDate() < pastDate.getDate())) {
            years--;
        }
        return years;
    }
    getMonthDiffBetweenDates(startDateStr, endDateStr) {
        const startDate = dayjs(startDateStr, 'YYYY-MM-DD');
        const endDate = dayjs(endDateStr, 'YYYY-MM-DD');
        return endDate.diff(startDate, 'month');
    }
    isDateInFuture(dateStr) {
        const today = this.getCurrentDate();
        const givenDate = new Date(dateStr);
        return givenDate.valueOf() > today.valueOf();
    }
    getEarlierDate(firstDateString, secondDateString) {
        const firstDate = new Date(firstDateString);
        const secondDate = new Date(secondDateString);
        return firstDate.valueOf() <= secondDate.valueOf() ? firstDateString : secondDateString;
    }
    getLaterDate(firstDateString, secondDateString) {
        const firstDate = new Date(firstDateString);
        const secondDate = new Date(secondDateString);
        return firstDate.valueOf() > secondDate.valueOf() ? firstDateString : secondDateString;
    }
    isFirstDateGreater(firstDateString, secondDateString) {
        const firstDate = new Date(firstDateString);
        const secondDate = new Date(secondDateString);
        return firstDate.valueOf() > secondDate.valueOf();
    }
    getPlanningHorizonDate(dateOfBirth, retirementAge, yearsInRetirement) {
        return this.addYearsToDateString(dateOfBirth, retirementAge + yearsInRetirement);
    }
    getDatePlus59AndHalfYears(date) {
        return this.add59AndHalfYears(date).toDate();
    }
    getDateOneDayBefore59AndHalfYears(date) {
        return this.add59AndHalfYears(date).subtract(1, 'days').toDate();
    }
    getLeapSafeMonthAndDay(dateString) {
        const dateComponents = dateString.split('-');
        const month = dateComponents[1];
        const day = dateComponents[2];
        return parseInt(month, 10) === 2 && parseInt(day, 10) === 29 ? `-${month}-28` : `-${month}-${day}`;
    }
    getMonthAndDay(dateString) {
        const dateComponents = dateString.split('-');
        const month = dateComponents[1];
        const day = dateComponents[2];
        return `-${month}-${day}`;
    }
    getYearToUse(year) {
        let yearToUse = year;
        if (isNil(year) || isNaN(Number(year)) || year === 0 || (isString(year) && /^\s*$/.test(year))) {
            yearToUse = this.getCurrentYear();
        }
        return `${yearToUse}`;
    }
    add59AndHalfYears(date) {
        const initialDate = dayjs(date);
        const resultDate = initialDate.add(59, 'years').add(6, 'months');
        return resultDate.date() < initialDate.date() ? resultDate.add(1, 'days') : resultDate;
    }
}
DateHelperService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: DateHelperService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
DateHelperService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: DateHelperService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: DateHelperService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

class GatekeeperService {
    constructor(falcor) {
        this.falcor = falcor;
        this.TOGGLES_TO_BATCH = Object.values(Features);
    }
    /**
     * Check the status of feature toggle by querying Gatekeeper.
     *  A batch query will be made if shouldBatchToggles is true and featureId is included in TOGGLES_TO_BATCH.
     *  Uses Falcor cache when available to avoid redundant network calls.
     * @param featureId feature to check
     * @param shouldBatchToggles whether checking a featureId in TOGGLES_TO_BATCH should result in a batch query
     * @returns a boolean indicating whether the toggle is enabled or FALSE in case of exception
     */
    async checkFeatureToPromise(featureId, shouldBatchToggles = false) {
        try {
            if (shouldBatchToggles && this.TOGGLES_TO_BATCH.includes(featureId)) {
                const features = await this.falcor.get({
                    path: FalcorRoutes.GATEKEEPER,
                    pathArgs: [this.TOGGLES_TO_BATCH.join(',')]
                });
                return features.find(feature => feature.featureId === featureId)?.enabled ?? false;
            }
            const feature = await this.falcor.get({
                path: FalcorRoutes.GATEKEEPER,
                pathArgs: [featureId]
            });
            return feature.enabled;
        }
        catch (error) {
            return false;
        }
    }
}
GatekeeperService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GatekeeperService, deps: [{ token: FalcorWrapperService }], target: i0.ɵɵFactoryTarget.Injectable });
GatekeeperService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GatekeeperService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GatekeeperService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: FalcorWrapperService }]; } });

class MaasService {
    constructor(falcorWrapperService) {
        this.falcorWrapperService = falcorWrapperService;
    }
    async send(key, ...data) {
        try {
            return await this.falcorWrapperService.call({
                path: FalcorRoutes.MEASUREMENT_AS_A_SERVICE,
                callParams: [{ key, data }]
            });
        }
        catch (error) {
            throw new Error(`Digital Advisor Common Library: Error while connecting to MaaS. ${error}`);
        }
    }
}
MaasService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MaasService, deps: [{ token: FalcorWrapperService }], target: i0.ɵɵFactoryTarget.Injectable });
MaasService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MaasService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MaasService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: FalcorWrapperService }]; } });

/* eslint-disable @typescript-eslint/no-explicit-any */
class WindowService {
    constructor(platformId) {
        this.platformId = platformId;
    }
    get() {
        return isPlatformBrowser(this.platformId)
            ? window
            : {
                document: undefined,
                innerWidth: 0,
                innerHeight: 0,
                pageYOffset: 0,
                dispatchEvent: () => true,
                location: empty(),
                print: noop,
                addEventListener: noop,
                removeEventListener: noop
            };
    }
}
WindowService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: WindowService, deps: [{ token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable });
WindowService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: WindowService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: WindowService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }]; } });

class MedalliaService {
    constructor(router, windowService, rendererFactory2) {
        this.router = router;
        this.windowService = windowService;
        this.rendererFactory2 = rendererFactory2;
        this.navigationURL = '';
        this.isWaitingForSurveyToClose = false;
        this.MEDALLIA_LOAD_TIME_BUFFER = 250;
        this.renderer = this.rendererFactory2.createRenderer(null, null);
        this.renderer.listen('window', 'MDigital_Form_Close_No_Submit', () => {
            this.navigateWhenSurveyIsClosed();
        });
        this.renderer.listen('window', 'MDigital_Form_Close_Submitted', () => {
            this.navigateWhenSurveyIsClosed();
        });
    }
    waitForSurveyCloseToNavigate(url) {
        this.navigationURL = url;
        setTimeout(() => {
            if (!this.windowService.get().KAMPYLE_ONSITE_SDK?.isSurveyDisplayed()) {
                this.navigate(this.navigationURL);
            }
            else {
                this.isWaitingForSurveyToClose = true;
            }
        }, this.MEDALLIA_LOAD_TIME_BUFFER);
    }
    navigateWhenSurveyIsClosed() {
        if (this.isWaitingForSurveyToClose) {
            this.navigate(this.navigationURL);
            this.isWaitingForSurveyToClose = false;
        }
    }
    navigate(url) {
        if (url.includes('http')) {
            this.windowService.get().location.href = url;
        }
        else {
            void this.router.navigateByUrl(url);
        }
    }
}
MedalliaService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MedalliaService, deps: [{ token: i1.Router }, { token: WindowService }, { token: i0.RendererFactory2 }], target: i0.ɵɵFactoryTarget.Injectable });
MedalliaService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MedalliaService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MedalliaService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.Router }, { type: WindowService }, { type: i0.RendererFactory2 }]; } });

class NavigationSpyService {
    constructor(router, locationStrategy) {
        this.router = router;
        this.locationStrategy = locationStrategy;
        this.isFirsTimeNavigationCompleted = false;
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.isFirsTimeNavigationCompleted = true;
                const mergeObjects = (res, current) => ({ ...res, ...current });
                this.previousUrl = this.currentPageUrlAfterRedirects;
                this.currentPageUrl = event.url;
                this.currentPageUrlAfterRedirects = event.urlAfterRedirects;
                this.currentRouteData = this.getRouteData(this.router.routerState.root.snapshot).reduce(mergeObjects);
            }
        });
    }
    getPreviousPageUrl() {
        return this.previousUrl;
    }
    getPageCurrentUrl() {
        return this.currentPageUrl;
    }
    getCurrentPageUrlAfterRedirects() {
        return this.currentPageUrlAfterRedirects;
    }
    getCurrentRouteData() {
        return this.currentRouteData;
    }
    isFirsTimeNavigationEnded() {
        return this.isFirsTimeNavigationCompleted;
    }
    getAppRootPath() {
        return this.locationStrategy.getBaseHref();
    }
    getRouteData(route) {
        if (!route.children.length) {
            return [route.data];
        }
        if (route.children.length > 1) {
            throw new Error('Not supported for the route config with more than one children!');
        }
        // Return route data in the "prototype view". Children data overwrite parent.
        return [route.data, ...this.getRouteData(route.children[0])];
    }
}
NavigationSpyService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: NavigationSpyService, deps: [{ token: i1.Router }, { token: i2$1.LocationStrategy }], target: i0.ɵɵFactoryTarget.Injectable });
NavigationSpyService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: NavigationSpyService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: NavigationSpyService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.Router }, { type: i2$1.LocationStrategy }]; } });

class UrlService {
    constructor(browserUtilsService, falcorService, windowService) {
        this.browserUtilsService = browserUtilsService;
        this.falcorService = falcorService;
        this.windowService = windowService;
        this.DA_SAT_HOSTNAME = 'satdigital-advisor.vanguard.com';
        this.MFES_WITH_FRONTEND_GENERATED_URLS = [
            ClientExposedUrl.WEBCOMPONENT_MY_PLAN_PAGE,
            ClientExposedUrl.WEBCOMPONENT_CONFIRMATION_PAGE,
            ClientExposedUrl.WEBCOMPONENT_EMERGENCY_SAVINGS,
            ClientExposedUrl.WEBCOMPONENT_SOCIAL_SECURITY_EXPERIENCE,
            ClientExposedUrl.WEBCOMPONENT_SOCIAL_SECURITY_EXPERIENCE_INACTIVE,
            ClientExposedUrl.WEBCOMPONENT_DAP_APPOINTMENT_SCHEDULER,
            ClientExposedUrl.WEBCOMPONENT_MONEY_TO_WORK,
            ClientExposedUrl.WEBCOMPONENT_ACTIVE_IN_ADVICE,
            ClientExposedUrl.BACKEND_MONEY_TO_WORK,
            ClientExposedUrl.WEBCOMPONENT_OFFER_UPGRADE_DOWNGRADE,
            ClientExposedUrl.WEBCOMPONENT_VMAP_POST_CONVERSION,
            ClientExposedUrl.WEBCOMPONENT_ACTIVE_RISK_ASSESSMENT,
            ClientExposedUrl.WEBCOMPONENT_PORTFOLIO_BUILDER,
            ClientExposedUrl.WEBCOMPONENT_ADVICE_FUTURE_TRANSACTION
        ];
    }
    async getUrl(urlKey, includePackage = true) {
        if (this.MFES_WITH_FRONTEND_GENERATED_URLS.includes(urlKey)) {
            return includePackage ? this.getMicroFrontendPkgUrl(urlKey) : this.getMicroFrontEndUrl(urlKey);
        }
        return this.falcorService.get({
            path: FalcorRoutes.URL,
            pathArgs: [urlKey],
            unpack: UnpackMode.FAIL_SAFE
        });
    }
    /**
     * @returns the DAEnvironment based on 'env' meta tag value in HTML
     */
    getEnvironment() {
        const envTag = this.browserUtilsService.getMetaTagValue('env')?.toLowerCase();
        if (envTag === DAEnvironment.DEV || envTag === DAEnvironment.SAT) {
            return envTag;
        }
        return DAEnvironment.PRODUCTION;
    }
    /**
     * @returns the MFE bundle using the browser window.location.hostname OR the DA_SAT_HOSTNAME if running locally
     */
    getMicroFrontendPkgUrl(urlKey) {
        const hostnameAndPath = this.getMicroFrontEndUrl(urlKey);
        const bundlePath = MFE_URLS[urlKey]?.BUNDLE_PATH ?? '';
        return `${hostnameAndPath}${bundlePath}`;
    }
    getHostname(urlKey) {
        let hostname;
        if (MFE_URLS[urlKey]?.HOSTNAME) {
            hostname = this.getEnvironment() === DAEnvironment.PRODUCTION ? MFE_URLS[urlKey].HOSTNAME.PROD : MFE_URLS[urlKey].HOSTNAME.TEST;
        }
        else {
            hostname = this.getEnvironment() === DAEnvironment.DEV ? this.DA_SAT_HOSTNAME : this.windowService.get().location.hostname;
        }
        return hostname;
    }
    getMicroFrontEndUrl(urlKey) {
        const hostname = this.getHostname(urlKey);
        const path = MFE_URLS[urlKey]?.PATH ?? '';
        return `https://${hostname}${path}`;
    }
}
UrlService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: UrlService, deps: [{ token: BrowserUtilsService }, { token: FalcorWrapperService }, { token: WindowService }], target: i0.ɵɵFactoryTarget.Injectable });
UrlService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: UrlService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: UrlService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: BrowserUtilsService }, { type: FalcorWrapperService }, { type: WindowService }]; } });

/**
 * Manages web component override state.
 * Keeps the state stored in localStorage while prividing
 * consumers a cleaner interface
 */
class WebComponentOverrideStateService {
    constructor(browserStorageUtilsService) {
        this.browserStorageUtilsService = browserStorageUtilsService;
    }
    get state() {
        return this.browserStorageUtilsService.getItemFromStorage(LocalStorageKeys.webComponentOverride);
    }
    set state(value) {
        this.browserStorageUtilsService.setItemToStorage(LocalStorageKeys.webComponentOverride, value);
    }
    get tNumber() {
        return this.browserStorageUtilsService.getItemFromStorage(LocalStorageKeys.tNumber);
    }
    set tNumber(value) {
        if (value) {
            this.browserStorageUtilsService.setItemToStorage(LocalStorageKeys.tNumber, value);
        }
        else {
            this.browserStorageUtilsService.clearLocalStorageKey(LocalStorageKeys.tNumber);
        }
    }
    get coderPrefix() {
        return this.browserStorageUtilsService.getItemFromStorage(LocalStorageKeys.coderPrefix);
    }
    set coderPrefix(value) {
        if (value) {
            this.browserStorageUtilsService.setItemToStorage(LocalStorageKeys.coderPrefix, value);
        }
        else {
            this.browserStorageUtilsService.clearLocalStorageKey(LocalStorageKeys.coderPrefix);
        }
    }
    /**
     * Gets a slice of the state specific to a webcomponent
     */
    getWebComponentState(urlKey) {
        return this.state?.[urlKey];
    }
}
WebComponentOverrideStateService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: WebComponentOverrideStateService, deps: [{ token: BrowserStorageUtilsService }], target: i0.ɵɵFactoryTarget.Injectable });
WebComponentOverrideStateService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: WebComponentOverrideStateService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: WebComponentOverrideStateService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: BrowserStorageUtilsService }]; } });

/**
 * Overrides hostname with the one from Coder.
 * Allows to run components being developed in Coder.
 */
class CoderOriginOverrideService {
    constructor(stateService) {
        this.stateService = stateService;
    }
    override(url) {
        const urlInstance = new URL(url);
        const coderPrefix = this.stateService.coderPrefix;
        if (coderPrefix) {
            urlInstance.host = `${coderPrefix}.coder.opst.c1.vanguard.com`;
            urlInstance.pathname = this.convertPathname(urlInstance.pathname);
        }
        return urlInstance.toString();
    }
    convertPathname(pathname) {
        if (pathname.includes('/pkg/')) {
            return pathname.slice(pathname.indexOf('/pkg/'));
        }
        return pathname.slice(pathname.lastIndexOf('/'));
    }
}
CoderOriginOverrideService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CoderOriginOverrideService, deps: [{ token: WebComponentOverrideStateService }], target: i0.ɵɵFactoryTarget.Injectable });
CoderOriginOverrideService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CoderOriginOverrideService, providedIn: 'any' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CoderOriginOverrideService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'any'
                }]
        }], ctorParameters: function () { return [{ type: WebComponentOverrideStateService }]; } });

/**
 * Overrides hostname with a predefined one from another environment
 */
class EnvironmentOriginOverrideService {
    constructor(stateService) {
        this.stateService = stateService;
    }
    override(url, urlKey) {
        const urlInstance = new URL(url);
        const state = this.stateService.getWebComponentState(urlKey);
        const environment = state.origin.env;
        const host = MFE_URLS[urlKey]?.HOSTNAME?.[environment];
        if (host) {
            urlInstance.host = host;
        }
        return urlInstance.toString();
    }
}
EnvironmentOriginOverrideService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: EnvironmentOriginOverrideService, deps: [{ token: WebComponentOverrideStateService }], target: i0.ɵɵFactoryTarget.Injectable });
EnvironmentOriginOverrideService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: EnvironmentOriginOverrideService, providedIn: 'any' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: EnvironmentOriginOverrideService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'any'
                }]
        }], ctorParameters: function () { return [{ type: WebComponentOverrideStateService }]; } });

class InactiveOriginOverrideService {
    override(url) {
        const urlInstance = new URL(url);
        const segments = urlInstance.pathname.split('/');
        segments[1] = `inactive-${segments[1]}`;
        urlInstance.pathname = segments.join('/');
        return urlInstance.toString();
    }
}
InactiveOriginOverrideService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: InactiveOriginOverrideService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
InactiveOriginOverrideService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: InactiveOriginOverrideService, providedIn: 'any' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: InactiveOriginOverrideService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'any'
                }]
        }] });

// claim ports on https://confluence.vanguard.com/display/EAUC/Micro-Frontends+Local+Port+Numbers
const localPorts = {
    [ClientExposedUrl.WEBCOMPONENT_MICROFRONTEND_EXEMPLAR]: 9000,
    [ClientExposedUrl.WEBCOMPONENT_NDA]: 9010,
    [ClientExposedUrl.WEBCOMPONENT_EMERGENCY_SAVINGS]: 9020,
    [ClientExposedUrl.WEBCOMPONENT_PERSONALIZED_PORTFOLIO]: 9030,
    [ClientExposedUrl.WEBCOMPONENT_RISKFLOW]: 9040,
    [ClientExposedUrl.WEBCOMPONENT_CASHFLOW]: 9050,
    [ClientExposedUrl.WEBCOMPONENT_CUSTOM_GOAL]: 9060,
    [ClientExposedUrl.WEBCOMPONENT_ACCOUNTS_AND_CONTRIBUTIONS]: 9070,
    [ClientExposedUrl.WEBCOMPONENT_FAQ]: 9080,
    [ClientExposedUrl.WEBCOMPONENT_CASH_FINDER]: 9090,
    [ClientExposedUrl.WEBCOMPONENT_FIRST_MONTH_EXPERIENCE]: 9100,
    [ClientExposedUrl.WEBCOMPONENT_FINANCIAL_PROFILE]: 9110,
    [ClientExposedUrl.WEBCOMPONENT_PORTFOLIO_CUSTOMIZATION]: 9120,
    [ClientExposedUrl.WEBCOMPONENT_ACCOUNTS_AND_HOLDINGS]: 9130,
    [ClientExposedUrl.WEBCOMPONENT_MEDICARE_MATCH]: 9140,
    [ClientExposedUrl.WEBCOMPONENT_HEALTH_CARE_COST_ESTIMATOR]: 9150,
    [ClientExposedUrl.WEBCOMPONENT_FINANCIAL_GOAL_TRADEOFFS]: 9160,
    [ClientExposedUrl.WEBCOMPONENT_SOCIAL_SECURITY_EXPERIENCE]: 9170,
    [ClientExposedUrl.WEBCOMPONENT_ACTIVITY_LOG]: 9180,
    [ClientExposedUrl.WEBCOMPONENT_DAP_APPOINTMENT_SCHEDULER]: 9190,
    [ClientExposedUrl.WEBCOMPONENT_PERFORMANCE]: 9200,
    [ClientExposedUrl.WEBCOMPONENT_MONEY_TO_WORK]: 9210,
    [ClientExposedUrl.WEBCOMPONENT_TRANSACTION_MANAGE_TRANSFERS]: 9220,
    [ClientExposedUrl.WEBCOMPONENT_ACTIVE_IN_ADVICE]: 9240,
    [ClientExposedUrl.WEBCOMPONENT_TOOLS_RESOURCES]: 9230,
    [ClientExposedUrl.WEBCOMPONENT_TAX_LOSS_OPTION]: 9250,
    [ClientExposedUrl.WEBCOMPONENT_REQUIRED_MINIMUM_DISTRIBUTION]: 9260,
    [ClientExposedUrl.WEBCOMPONENT_DATA_AGGREGATION]: 9270,
    [ClientExposedUrl.WEBCOMPONENT_OFFER_UPGRADE_DOWNGRADE]: 9280
};
/**
 * Overrides host with tNumber.vanguard.com:localPort,
 * so that a component can be loaded from local dev server
 */
class LocalOriginOverrideService {
    constructor(stateService) {
        this.stateService = stateService;
    }
    override(url, urlKey) {
        const urlInstance = new URL(url);
        const tNumber = this.stateService.tNumber;
        if (!tNumber)
            return url;
        urlInstance.host = `${tNumber}.vanguard.com`;
        urlInstance.port = String(MFE_URLS[urlKey]?.LOCAL_BACKEND_PORT ?? localPorts[urlKey]);
        urlInstance.pathname = this.convertPathname(urlInstance.pathname);
        return urlInstance.toString();
    }
    convertPathname(pathname) {
        if (pathname.includes('/pkg/')) {
            return pathname.slice(pathname.indexOf('/pkg/'));
        }
        return pathname.slice(pathname.lastIndexOf('/'));
    }
}
LocalOriginOverrideService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: LocalOriginOverrideService, deps: [{ token: WebComponentOverrideStateService }], target: i0.ɵɵFactoryTarget.Injectable });
LocalOriginOverrideService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: LocalOriginOverrideService, providedIn: 'any' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: LocalOriginOverrideService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'any'
                }]
        }], ctorParameters: function () { return [{ type: WebComponentOverrideStateService }]; } });

/**
 * Passes through the incoming url without override.
 * Is used by default when override is not set
 */
class OpaqueOriginOverrideService {
    override(url) {
        return url;
    }
}
OpaqueOriginOverrideService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: OpaqueOriginOverrideService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
OpaqueOriginOverrideService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: OpaqueOriginOverrideService, providedIn: 'any' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: OpaqueOriginOverrideService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'any'
                }]
        }] });

/**
 * Maps override type to implementation class
 */
const serviceMap = {
    local: LocalOriginOverrideService,
    coder: CoderOriginOverrideService,
    inactive: InactiveOriginOverrideService,
    env: EnvironmentOriginOverrideService,
    default: OpaqueOriginOverrideService
};
/**
 * Overrides component origin depending on the config
 * taken from localStorage
 */
class OriginOverrideService {
    constructor(stateService, injector) {
        this.stateService = stateService;
        this.injector = injector;
    }
    override(url, urlKey) {
        const state = this.stateService.getWebComponentState(urlKey);
        const instance = this.getInstance(state?.origin?.strategy);
        return instance.override(url, urlKey);
    }
    getInstance(overrideType) {
        const constructor = serviceMap[overrideType ?? 'default'];
        return this.injector.get(constructor);
    }
}
OriginOverrideService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: OriginOverrideService, deps: [{ token: WebComponentOverrideStateService }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable });
OriginOverrideService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: OriginOverrideService, providedIn: 'any' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: OriginOverrideService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'any' }]
        }], ctorParameters: function () { return [{ type: WebComponentOverrideStateService }, { type: i0.Injector }]; } });

/**
 * Overrides package name (bundle) if specified
 */
class PackageOverrideService {
    constructor(overrideStateService) {
        this.overrideStateService = overrideStateService;
    }
    override(url, urlKey) {
        const state = this.overrideStateService.getWebComponentState(urlKey);
        if (!state?.package)
            return url;
        const urlInstance = new URL(url);
        const segments = urlInstance.pathname.split('/');
        segments[segments.length - 1] = state.package;
        urlInstance.pathname = segments.join('/');
        return urlInstance.toString();
    }
}
PackageOverrideService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: PackageOverrideService, deps: [{ token: WebComponentOverrideStateService }], target: i0.ɵɵFactoryTarget.Injectable });
PackageOverrideService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: PackageOverrideService, providedIn: 'any' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: PackageOverrideService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'any' }]
        }], ctorParameters: function () { return [{ type: WebComponentOverrideStateService }]; } });

/* eslint-disable no-param-reassign */
class WebComponentOverrideService {
    constructor(originOverride, packageOverride) {
        this.originOverride = originOverride;
        this.packageOverride = packageOverride;
    }
    override(url, urlKey) {
        url = this.originOverride.override(url, urlKey);
        url = this.packageOverride.override(url, urlKey);
        return url;
    }
}
WebComponentOverrideService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: WebComponentOverrideService, deps: [{ token: OriginOverrideService }, { token: PackageOverrideService }], target: i0.ɵɵFactoryTarget.Injectable });
WebComponentOverrideService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: WebComponentOverrideService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: WebComponentOverrideService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: OriginOverrideService }, { type: PackageOverrideService }]; } });

/* eslint-disable @typescript-eslint/no-unnecessary-condition */
class WebComponentLoadService {
    constructor(ssrService, urlService, overrideService) {
        this.ssrService = ssrService;
        this.urlService = urlService;
        this.overrideService = overrideService;
    }
    async loadComponent(urlKey) {
        return this.ssrService.skipSSRfor(async () => {
            const webComponentUrl = await this.getUrl(urlKey);
            return this.loadWithRetry(webComponentUrl);
        }, Promise.resolve(undefined));
    }
    async getBaseUrl(urlKey) {
        const url = await this.urlService.getUrl(urlKey, false);
        const overrideUrl = this.overrideService.override(url, urlKey);
        if (overrideUrl !== 'https://' && overrideUrl !== 'http://') {
            return overrideUrl?.includes('/') ? overrideUrl.substring(0, overrideUrl.lastIndexOf('/')) : overrideUrl;
        }
        else {
            return url;
        }
    }
    async getUrl(urlKey) {
        const url = await this.urlService.getUrl(urlKey);
        return this.overrideService.override(url, urlKey);
    }
    async loadWithRetry(webComponentUrl, tryCount = 1) {
        const maxRetries = 7;
        try {
            return await this.lazyLoadWebComponent(webComponentUrl);
        }
        catch (e) {
            if (tryCount >= maxRetries) {
                throw e;
            }
            // delay increases exponentially (exponential backoff approach) 2 powered tryCount times 100
            // Example: tryCount = 0/delay = 100; tryCount = 1/delay = 200; tryCount = 2/delay = 400 and so on
            await delay(2 ** tryCount * 100);
            return this.loadWithRetry(webComponentUrl, tryCount + 1);
        }
    }
    async lazyLoadWebComponent(url) {
        const id = url.replace(/[:\\/.-]/g, '');
        let script = document.getElementById(id);
        if (script) {
            return script.wclPromise ?? Promise.resolve();
        }
        script = document.createElement('script');
        const result = new Promise((resolve, reject) => {
            const startTime = new Date().getTime();
            script.src = url;
            script.id = id;
            script.type = 'module';
            script.onload = resolve;
            script.onerror = () => {
                document.getElementById(id)?.remove();
                const endTime = new Date().getTime();
                reject(`Failed to load script from: ${url} after ${endTime - startTime}ms`);
            };
            document.head.appendChild(script);
        });
        script.wclPromise = result;
        return result;
    }
}
WebComponentLoadService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: WebComponentLoadService, deps: [{ token: SSRService }, { token: UrlService }, { token: WebComponentOverrideService }], target: i0.ɵɵFactoryTarget.Injectable });
WebComponentLoadService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: WebComponentLoadService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: WebComponentLoadService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: SSRService }, { type: UrlService }, { type: WebComponentOverrideService }]; } });

class ContentPipe {
    constructor(contentService, themeService) {
        this.contentService = contentService;
        this.themeService = themeService;
        this.fallback = '';
    }
    transform(path, flow) {
        const theme = this.themeService.getThemeFromMetaTag();
        return from(this.contentService.getContent(flow, theme)).pipe(map$1(content => {
            return get(content, path) ?? this.fallback;
        }));
    }
}
ContentPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ContentPipe, deps: [{ token: ContentService }, { token: ThemeService }], target: i0.ɵɵFactoryTarget.Pipe });
ContentPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ContentPipe, name: "content" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ContentPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'content'
                }]
        }], ctorParameters: function () { return [{ type: ContentService }, { type: ThemeService }]; } });

class StaticImagesPipe {
    transform(relativeUrl) {
        return relativeUrl ? `https://asw-digital-advisor-static.web.vanguard.com/images/${relativeUrl}` : undefined;
    }
}
StaticImagesPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: StaticImagesPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
StaticImagesPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: StaticImagesPipe, name: "staticImages" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: StaticImagesPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'staticImages'
                }]
        }] });

const pipes = [StaticImagesPipe, ContentPipe];
class CommonPipesModule {
}
CommonPipesModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CommonPipesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CommonPipesModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CommonPipesModule, declarations: [StaticImagesPipe, ContentPipe], exports: [StaticImagesPipe, ContentPipe] });
CommonPipesModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CommonPipesModule, providers: [ContentService, ThemeService, ContentPipe] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: CommonPipesModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...pipes],
                    exports: [...pipes],
                    providers: [ContentService, ThemeService, ContentPipe]
                }]
        }] });

/*
 * Public API Surface of vg-digital-advisor-common
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AmountHelper, BrowserStorageUtilsService, BrowserUtilsService, CommonPipesModule, ContentPipe, ContentService, DateHelper, DateHelperService, FalcorWrapperService, GatekeeperService, LocalStorageService, MF_CONTENT_PATH, MaasService, MedalliaService, NavigationSpyService, PercentageCalculator, SSRService, SessionStorageService, StaticImagesPipe, StringHelper, ThemeService, UrlService, WebComponentLoadDetailsHelper, WebComponentLoadService, WebComponentOverrideService, WebComponentOverrideStateService, WindowService, addOrReplaceElementInArray, assertIsDefined, assertIsNotString, assertIsString, assertOneOfEnumKeys, asyncForEach, copyArrayAndReplaceAtIndex, copyArrayOmittingIndexes, defineArray, delay, empty, equalIgnoreCase, filter, flattenJoinArrays, forEach, getByPath, getEnumValues, identifyTheme, interpolate, isFunction, isNil, isNumber, isOneOfEnumKeys, isPromise, isString, loadAsyncObjectProps, map, noop, not, notNil, reduce, replaceBrandNameTokens, replaceThemeMetaTag, replaceTitleTag, runIf, some, tokens };
